import ApiDrivenModel from "./ApiDrivenModel";
import ProposalItem from "./ProposalItem";
import Catalog from "./Catalog";

const PROPOSAL_FULL_CATALOG_ACCESSES = Object.freeze(new Set([true, "full"]));
const PROPOSAL_CHANGE_CATALOG_ACCESSES = Object.freeze(new Set([true, "full", "modify"]));
const PROPOSAL_VIEW_CATALOG_ACCESSES = Object.freeze(new Set([true, "full", "modify", "view"]));

export default class Proposal extends ApiDrivenModel {
	static resourceName = 'Proposal';
	
	static HasFullAccess(catalog) {
		return !Catalog.IsPublic(catalog) && PROPOSAL_FULL_CATALOG_ACCESSES.has(catalog.options?.proposals?.access);
	}
	
	static CanChange(catalog) {
		return !Catalog.IsPublic(catalog) && PROPOSAL_CHANGE_CATALOG_ACCESSES.has(catalog.options?.proposals?.access);
	}
	
	static CanView(catalog) {
		return !Catalog.IsPublic(catalog) && PROPOSAL_VIEW_CATALOG_ACCESSES.has(catalog.options?.proposals?.access);
	}

	constructor(properties) {
		super(properties);

		// Setup children
		if (this.items) {
			this.items.forEach((proposal_item, index) => {
				this.items[index] = new ProposalItem(proposal_item);
				this.items[index].addEventListener("change", () => {
					this.updateData("items", this.items);
				});
			});
			this.updateData("items", this.items);
		}
	}

	toData() {
		const items = this.items;
		const data = super.toData();
		data.items = items;
		return data;
	}

	cleanForSave() {
		super.cleanForSave();

		this.sales_order?.cleanForSave();
		this.items?.forEach(item => item.cleanForSave?.());
	}

	getCurrencySymbol() {
		return this.alternate_currency?.currency || this.customer?.currency?.currency;
	}

	/**
	 * @param {ProposalItem|Item|Number} item
	 * @returns Proposal
	 */
	addItem(item) {
		this.addFrom(item);
		return this;
	}

	/**
	 * @param {ProposalItem|Item|Number} item
	 * @returns ProposalItem
	 */
	addFrom(item) {
		let itemToAdd;
		if (item) {
			this.items ??= [];
			const proposalItem = ProposalItem.Make(item);
			const existingItem = this.items.find(it => it.item.id === proposalItem.item.id);
			itemToAdd = ProposalItem.Make(existingItem ?? proposalItem);
			itemToAdd.proposal = this.id;
			// if item is proposal item it prob has quantity set so only set if shortcut used
			if (!(item instanceof ProposalItem)) {
				itemToAdd.quantity = 1;
			}
			// just in case
			itemToAdd.quantity ??= 1;
			if (!existingItem) {
				this.items.push(itemToAdd);
			}
		}
		return itemToAdd;
	}

	/**
	 * @param {Array<Item|Number>} items
	 */
	addItems(items) {
		items?.forEach(it => this.addItem(it));
		return this;
	}

	async removeItem(proposalItem) {
		const index = this.items.findIndex((cur) => cur.id === proposalItem.id);
		if (index !== -1) {
			this.items.splice(index, 1);
			await proposalItem.remove();
		}
	}

	static ENDPOINT = "/Proposal";

	static SEARCH_PATHS = {
		auto_id: true,
		alternate_currency: true,
		approved: true,
		accepted: true,
		consented: true,
		currency: {
			prefix: true
		},
		customer_reference_number: true,
		created_on: true,
		id: true,
		items: ProposalItem.PATHS,
		name: true,
		updated_on: true,
		xls: true,
		pdf: true
	};

	static LOAD_PATHS = {
		auto_id: true,
		alternate_currency: true,
		approved: true,
		accepted: true,
		consented: true,
		currency: {
			prefix: true
		},
		customer_reference_number: true,
		created_on: true,
		id: true,
		items: ProposalItem.PATHS,
		customer: {
			id: true,
			locations: {
				id: true,
				label: true,
			},
			currency: {
				id: true,
				currency: true,
				currency_label: true,
			},
		},
		name: true,
		shipping_method: true,
		updated_on: true,
		xls: true,
		pdf: true
	};
}
